import React from 'react';
import FeaturePageShell from 'components/shell/FeaturePageShell';
import { Box, CircularProgress, Grid } from '@material-ui/core';
import { useCloudHomeData } from './hooks/useCloudHomeData';
import styles from './HomeScreen.module.scss';
import IntegrationBanner from './components/IntegrationBanner';
import ServiceStatus from './components/ServiceStatus';
import SetupGuide from './components/SetupGuide';
import PopularQueriesWidget from './components/PopularQueriesWidget';
import PopularProductsWidget from './components/PopularProductsWidget';
import { CatalogStatusWidget } from '../catalog-sync';
import { useLocalizedMessage } from '../../util/useLocalizedMessage';
import CloudHomeMetrics from './components/CloudHomeMetrics';
import CloudOperationsPanel from '../operation-supervisor/CloudOperationsPanel';

/**
 * Standalone cloud Home.
 *
 * This deliberately retains the cloud product's dashboard and onboarding
 * composition. Transport-neutral plugin pages may be reused inside cloud, but
 * they must never replace cloud-only product guidance or analytics.
 */
export default function CloudHomeScreen() {
  const localizeMessage = useLocalizedMessage();
  const {
    shopId,
    isLoading,
    isServiceActive,
    isServiceUpdating,
    wasInitiallyActive,
    toggleService,
    setupProgress,
    queryPopular,
    popularByView,
  } = useCloudHomeData();

  const setupComplete = setupProgress.connected
    && setupProgress.integration
    && setupProgress.activated;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <FeaturePageShell
      title={localizeMessage('cloud.home.title')}
      description={localizeMessage('cloud.home.description')}
      width="expanded"
    >
      <div data-testid="cloud-home">

        {!isLoading && (
          <IntegrationBanner
            isCompleted={setupProgress.integration}
            shopId={shopId}
          />
        )}

        <div className={styles.analyticsHeader}>
          <h2>{localizeMessage('cloud.home.analytics.title')}</h2>
          <span>{localizeMessage('daterange.last30days')}</span>
        </div>

        <div className={styles.homeMetrics}>
          <CloudHomeMetrics queryPopular={queryPopular} popularByView={popularByView} />
        </div>

        <div className={styles.grid}>
          <div className={styles.mainColumn}>
            {!isLoading && !setupComplete && (
              <SetupGuide progress={setupProgress} shopId={shopId} />
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <PopularQueriesWidget queryPopular={queryPopular} currentShop={shopId} />
              </Grid>
              <Grid item xs={12} md={6}>
                <PopularProductsWidget popularByView={popularByView} currentShop={shopId} />
              </Grid>
            </Grid>
          </div>

          <div className={styles.sideColumn}>
            <CloudOperationsPanel />
            {!isLoading && !wasInitiallyActive && (
              <div className={styles.serviceStatusWrapper}>
                <ServiceStatus
                  isActive={isServiceActive}
                  onToggle={toggleService}
                  isLoading={isServiceUpdating}
                  wasInitiallyActive={wasInitiallyActive}
                />
              </div>
            )}

            <CatalogStatusWidget />
          </div>
        </div>
      </div>
    </FeaturePageShell>
  );
}
